import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import ExperienceButton from '../Quests/ExperienceButton'

const KNOWN_RATIOS = [
  { a: 1, label: '1:1' },
  { a: 2 / 1, label: '2:1' },
  { a: 1024 / 465, label: '1024:465' },
  { a: 5 / 2, label: '5:2' },
  { a: 16 / 9, label: '16:9' },
  { a: 85 / 186, label: '85:186' },
]

const ratioLabelFromAspect = (aspect) => {
  if (typeof aspect !== 'number' || !(aspect > 0)) return null
  const match = KNOWN_RATIOS.find((k) => Math.abs(k.a - aspect) < 0.05)
  if (match) return match.label
  const precision = 1000
  let num = Math.round(aspect * precision)
  let den = precision
  const g = (a, b) => (b ? g(b, a % b) : a)
  const d = g(num, den) || 1
  num = Math.round(num / d)
  den = Math.round(den / d)
  if (den > 20) {
    return aspect.toFixed(2).replace(/\.?0+$/, '')
  }
  return `${num}:${den}`
}

const getCenteredAspectCrop = (mediaWidth, mediaHeight, aspect) => {
  if (typeof aspect === 'number' && aspect > 0) {
    const imageAspect = mediaWidth / mediaHeight
    let cropWidth = 100
    let cropHeight = 100

    if (imageAspect > aspect) {
      cropWidth = (aspect / imageAspect) * 100
      cropHeight = 100
    } else {
      cropWidth = 100
      cropHeight = (imageAspect / aspect) * 100
    }

    const aspectCrop = makeAspectCrop(
      {
        unit: '%',
        width: cropWidth,
        height: cropHeight,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    )

    return centerCrop(aspectCrop, mediaWidth, mediaHeight)
  }

  return {
    unit: '%',
    x: 10,
    y: 10,
    width: 80,
    height: 60,
  }
}

const ImageCropModal = ({
  show,
  onHide,
  file,
  onConfirm,
  isUploading,
  aspect,
  title = 'Crop image',
  confirmLabel = 'Apply crop',
  ratioLabel: ratioLabelProp,
}) => {
  const imageRef = useRef(null)
  const containerRef = useRef(null)
  const [imageUrl, setImageUrl] = useState('')
  const [crop, setCrop] = useState()
  const [completedCrop, setCompletedCrop] = useState(null)
  const ratioLabel = ratioLabelProp || ratioLabelFromAspect(aspect) || null

  useEffect(() => {
    if (!file || !show) {
      setImageUrl('')
      setCrop(undefined)
      setCompletedCrop(null)
      return undefined
    }
    const nextUrl = URL.createObjectURL(file)
    setImageUrl(nextUrl)
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
      containerRef.current.scrollLeft = 0
    }
    return () => URL.revokeObjectURL(nextUrl)
  }, [file, show])

  const onImageLoad = (event) => {
    const { width, height } = event.currentTarget
    if (containerRef.current) {
      containerRef.current.scrollTop = 0
      containerRef.current.scrollLeft = 0
    }
    setCrop(getCenteredAspectCrop(width, height, aspect))
  }

  const handleConfirm = async () => {
    if (!file) return

    if (!completedCrop || !imageRef.current) {
      onConfirm(file)
      return
    }

    const scaleX = imageRef.current.naturalWidth / imageRef.current.width
    const scaleY = imageRef.current.naturalHeight / imageRef.current.height
    const outputWidth = Math.max(1, Math.round(completedCrop.width * scaleX))
    const outputHeight = Math.max(1, Math.round(completedCrop.height * scaleY))

    const canvas = document.createElement('canvas')
    canvas.width = outputWidth
    canvas.height = outputHeight
    const context = canvas.getContext('2d')
    if (!context) return

    context.drawImage(
      imageRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height,
    )

    canvas.toBlob(
      (blob) => {
        if (!blob) return
        const croppedFile = new File([blob], file.name, {
          type: blob.type || file.type || 'image/jpeg',
        })
        onConfirm(croppedFile)
      },
      file.type || 'image/jpeg',
      0.95,
    )
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      className='image-crop-modal-dialog'
      dialogClassName='experience-crop-dialog'
      contentClassName='experience-crop-card'
      backdropClassName='experience-crop-backdrop'>
      <div
        className='experience-crop-modal'
        data-ratio={ratioLabel || undefined}>
        <h2 className='experience-crop-title'>{title}</h2>
        {ratioLabel ? (
          <p className='experience-crop-lead'>
            Crop to <b>{ratioLabel}</b>, then apply.
          </p>
        ) : (
          <p className='experience-crop-lead'>Adjust the crop, then apply.</p>
        )}
        <div ref={containerRef} className='image-crop-modal-container'>
          {imageUrl ? (
            <ReactCrop
              className='image-crop-modal'
              crop={crop}
              onChange={(_, percentCrop) => {
                setCrop(percentCrop)
              }}
              onComplete={(nextCrop) => {
                setCompletedCrop(nextCrop)
              }}
              aspect={
                typeof aspect === 'number' && aspect > 0 ? aspect : undefined
              }
              minWidth={120}
              keepSelection>
              <img
                ref={imageRef}
                src={imageUrl}
                alt='Crop source'
                onLoad={onImageLoad}
                className='image-crop-modal-image'
              />
            </ReactCrop>
          ) : null}
        </div>
        <div className='experience-crop-actions'>
          <ExperienceButton
            variant='outline'
            onClick={onHide}
            disabled={isUploading}>
            Cancel
          </ExperienceButton>
          <ExperienceButton
            variant='primary'
            isLoading={isUploading}
            disabled={isUploading || !file}
            onClick={handleConfirm}>
            {confirmLabel}
          </ExperienceButton>
        </div>
      </div>
    </Modal>
  )
}

export default ImageCropModal
